import { getEventTimestamp } from '../../../../components/shared/get-event-timestamp'
import { parseMarkers } from '../../../../components/shared/map'
import store, {
    EVENTS_FRONT_ELEMENTS,
} from '../../../../admin/store'
import { EVENT_TAG_ONLINE } from '../../../../consts'

const config = window.APP_CONFIG
export const ITEMS_PER_PAGE = 30

/**
 * Get running events count query for take count of running events from API
 *
 * @param {string|null} query
 * @return {AxiosPromise}
 */
export const runningEventsCountQuery = (query = null) => window.axios.get(`${config.REACT_APP_API_URL}api/events/count?publishable=true&system=${config.REACT_APP_SYSTEM}&dateType=running${query ? '&' + replaceFilterDate(query) : ''}`)

/**
 * Replace taking from string
 *
 * @param {string} filter
 * @param {string} replace
 * @return {string}
 */
export const replaceFilterDate = (filter, replace = 'dateType=running') => filter ? filter.replace('take=next', replace) : ''

/**
 * Map event result to specific formatted data
 *
 * @param {Object} eventsResult
 * @return {*}
 */
const mapEvents = eventsResult => eventsResult.data.map(event => {
    let mappingEvent = { ...event }

    mappingEvent.isOnline = mappingEvent?.tags?.data?.some?.(tag => tag.id === EVENT_TAG_ONLINE) || false

    return {
        [getEventTimestamp(mappingEvent)]: mappingEvent,
    }
})

/**
 * Map events result to object
 *
 * @param {boolean} loadRunning
 * @param {Object} props
 * @return {Object}
 */
export const mapEventsResult = (loadRunning, props) => {
    let events = []

    let {
        result,
        isFilter,
        eventFilter,
    } = props

    const eventsResult = result?.[0]?.data
    const totalPrev = result?.[1]?.data?.data?.total || null
    const runningEvents = loadRunning && result?.[2]?.data?.data?.length > 0 ? result?.[2]?.data?.data : []
    const runningEventsTotal = loadRunning && result?.[3]?.data ? result?.[3]?.data?.data?.total : null
    const eventsState = store.getState().eventsFront.events.events

    if (!isFilter) {
        if (eventsResult.data.length) {
            events = Object.assign(
                eventsState,
                ...mapEvents(eventsResult),
            )
        } else {
            events = eventsState
        }
    } else {
        events = Object.assign(
            {},
            ...mapEvents(eventsResult)
        )
    }

    const cursor = eventsResult.meta.cursor.next

    let resultObject = {
        prev: {
            total: totalPrev,
        },
        events: {
            cursor: cursor,
            eventFilter: eventFilter,
            events: events,
            canLoadMore: cursor !== null,
        },
    }

    if (loadRunning) {
        resultObject = {
            ...resultObject,
            running: {
                events: runningEvents,
                total: runningEventsTotal,
            },
        }
    }

    return resultObject
}

/**
 * Load tournament events data and set it into store
 *
 * @param {Object} props
 * @returns {Promise<AxiosResponse<any> | never>}
 */
export const loadTournamentEvents = props => {
    const {
        defaultTag,
        limit,
    } = props

    const {
        REACT_APP_API_URL,
        REACT_APP_SYSTEM,
    } = config

    const { cursor } = store.getState().eventsFront.tournaments

    const includes = [
        'sports',
        'locations',
        'additionalInfo',
        'headerImage',
    ].join(',')

    let params = [
        `cursor=${cursor || 0}`,
        `tz=${moment.tz.guess()}`,
        `system=${REACT_APP_SYSTEM}`,
        `limit=${limit || ITEMS_PER_PAGE}`,
        `include=${includes}`,
        `tags=${defaultTag}`,
        'sort=created_at|desc',
    ].filter(param => param && param !== 'null')

    return window.axios.get(`${REACT_APP_API_URL}api/events?${params.join('&')}`)
        .then(result => {
            const eventsResult = {
                events: result?.data?.data,
            }

            store.dispatch({
                type: 'EVENTS_SET_DATA',
                element: 'tournaments',
                data: eventsResult,
            })
        })
}

/**
 * Load events data and set it into store
 *
 * @param {Object} props
 * @param {string} eventFilter
 * @param {boolean} isFilter
 * @param {boolean} loadRunning
 * @param {boolean} systemFilter
 * @return {Promise<any>}
 */
export const loadEvents = (props, eventFilter, isFilter = false, loadRunning = true, systemFilter = true) => {
    const {
        defaultTag,
        numActualEvents,
        showMap,
        limit,
    } = props

    const {
        REACT_APP_API_URL,
        REACT_APP_SYSTEM,
        REACT_APP_DEFAULT_CITY_FILTER_NAME,
        REACT_APP_PASS_MARKERS_TO_CITY,
    } = config

    const {
        busy,
        canLoadMore,
        cursor,
        initLoad,
    } = store.getState().eventsFront.events

    if (busy) {
        return new Promise(resolve => resolve())
    }

    EVENTS_FRONT_ELEMENTS.forEach(element => {
        store.dispatch({
            type: 'EVENTS_SET_BUSY',
            element: element,
            busy: true,
        })
    })

    if (isFilter || canLoadMore) {
        const includes = [
            'sports',
            'locations',
            'additionalInfo',
            'headerImage',
            'tags',
            'rosterPositions',
            'organisingGroup',
        ].join(',')

        if (!eventFilter || eventFilter === '') {
            eventFilter = 'take=next'
        }

        if (defaultTag && !eventFilter.includes('tags=')) {
            eventFilter = eventFilter.concat(`&tags=${defaultTag}`)
        }

        let params = [
            `cursor=${cursor || 0}`,
            `tz=${moment.tz.guess()}`,
            `limit=${limit || ITEMS_PER_PAGE}`,
            `${eventFilter}`,
            `include=${includes}`,
        ]

        if (systemFilter) {
            params.push(`system=${REACT_APP_SYSTEM}`)
        }

        params = params.filter(param => param && param !== 'null')

        if (REACT_APP_DEFAULT_CITY_FILTER_NAME) {
            params.push(`city=${REACT_APP_DEFAULT_CITY_FILTER_NAME}`)
        }

        const requests = [
            window.axios.get(`${REACT_APP_API_URL}api/events?${params.join('&')}`),
        ]

        if (initLoad || (eventFilter && eventFilter.toLocaleLowerCase().indexOf('date') === -1)) {
            requests.push(window.axios.get(`${REACT_APP_API_URL}api/events/count?dateType=prev&${replaceFilterDate(eventFilter, '')}&tz=${moment.tz.guess()}&system=${REACT_APP_SYSTEM}${REACT_APP_DEFAULT_CITY_FILTER_NAME ? `&city=${REACT_APP_DEFAULT_CITY_FILTER_NAME}` : ''}`))
            if (loadRunning) {
                requests.push(window.axios.get(`${REACT_APP_API_URL}api/events?include=${includes}&dateType=running&limit=${(numActualEvents || 4) + 1}&${replaceFilterDate(eventFilter)}&cursor=0&tz=${moment.tz.guess()}&system=${REACT_APP_SYSTEM}${REACT_APP_DEFAULT_CITY_FILTER_NAME ? `&city=${REACT_APP_DEFAULT_CITY_FILTER_NAME}` : ''}`))
                requests.push(runningEventsCountQuery(eventFilter))
            }
        }

        return window.axios.all(requests)
            .then(result => {
                const mappedEvents = mapEventsResult(loadRunning,{
                    result,
                    isFilter,
                    eventFilter,
                })

                EVENTS_FRONT_ELEMENTS.forEach(element => {
                    let currentEvents = null

                    switch (element) {
                        case 'running':
                            currentEvents = mappedEvents.running
                            break
                        case 'prev':
                            currentEvents = mappedEvents.prev
                            break
                        case 'events':
                            currentEvents = mappedEvents.events
                            break
                    }

                    if (currentEvents) {
                        store.dispatch({
                            type: 'EVENTS_SET_DATA',
                            element: element,
                            data: currentEvents,
                        })
                    }

                    store.dispatch({
                        type: 'EVENTS_SET_INIT_LOAD',
                        element: element,
                        initLoad: false,
                    })
                })

                if (showMap) {
                    const markers = parseMarkers({ data: mappedEvents.events.events }, 'event', REACT_APP_PASS_MARKERS_TO_CITY)
                    store.dispatch({
                        type: 'EVENTS_SET_MARKERS',
                        element: 'events',
                        markers: markers,
                    })
                }

                EVENTS_FRONT_ELEMENTS.forEach(element => {
                    store.dispatch({
                        type: 'EVENTS_SET_BUSY',
                        element: element,
                        busy: false,
                    })
                })
            })
    }

    return new Promise(resolve => resolve())
}

/**
 * Load events for events slider
 * @param {string} type                 - actual | next | past
 * @param {number} cursor               - cursor
 * @param {number} limit                - limit of loaded events
 * @param {string} eventsFilter         - events filter
 * @param {boolean} allSystems          - show events through all systems
 * @param {boolean} withCountRunning    - add count running to request?
 * @return {Promise}
 */
export const loadEventsForSlider = (type, cursor, limit, eventsFilter = '', allSystems = false, withCountRunning = true) => {
    const {
        REACT_APP_API_URL,
        REACT_APP_SYSTEM,
        REACT_APP_DEFAULT_CITY_FILTER_NAME,
    } = config

    const requests = []

    eventsFilter = (eventsFilter && typeof eventsFilter === 'string') ? eventsFilter.replace('take=next', '') : eventsFilter

    let dateType = 'running'

    switch (type) {
        case 'next':
            dateType = 'next'
            break
        case 'past':
            dateType = 'prev&history=true'
            break
        case 'online':
            dateType = 'next'
            break
    }

    const includes = [
        'sports',
        'locations',
        'additionalInfo',
        'headerImage',
    ].join(',')

    const systemUrl = allSystems ? '' : `&system=${REACT_APP_SYSTEM}`
    const cityUrl = REACT_APP_DEFAULT_CITY_FILTER_NAME ? `&city=${REACT_APP_DEFAULT_CITY_FILTER_NAME}` : ''
    const tzUrl = `&tz=${moment.tz.guess()}`
    const additionalFilterUrl = type === 'online' ? `&tags=${EVENT_TAG_ONLINE}` : ''

    if (limit) {
        requests.push(window.axios.get(`${REACT_APP_API_URL}api/events?dateType=${dateType}${systemUrl}${cityUrl}${tzUrl}${additionalFilterUrl}&include=${includes}&limit=${limit + 1}&cursor=${cursor || 0}&${eventsFilter}`))

        if (withCountRunning) {
            requests.push(window.axios.get(`${REACT_APP_API_URL}api/events/count?publishable=true&dateType=running${systemUrl}${cityUrl}${additionalFilterUrl}&${eventsFilter}`))
        }
    }

    if (!requests) {
        return new Promise(resolve => resolve())
    }

    return window.axios.all(requests)
        .then(result => {
            const events = result[0]?.data?.data
            const runningEventsTotal = result[1]?.data?.data?.total

            let typeEvents = ''

            switch (type) {
                case 'actual':
                    typeEvents = 'running'
                    break
                case 'next':
                    typeEvents = 'events'
                    break
                case 'past':
                    typeEvents = 'prev'
                    break
                case 'online':
                    typeEvents = 'online'
                    break
            }

            let mappedEvents = {
                events: events,
            }

            if (typeEvents === 'running') {
                mappedEvents = {
                    ...mappedEvents,
                    total: runningEventsTotal,
                }
            }

            store.dispatch({
                type: 'EVENTS_SET_DATA',
                element: typeEvents,
                data: mappedEvents,
            })

            return new Promise(resolve => resolve(result))
        })
}

/**
 * Load events for swiper slider
 * @param {string} type                 - actual | next | past
 * @param {number} cursor               - cursor
 * @param {number} limit                - limit of loaded events
 * @param {string} eventsFilter         - events filter
 * @param {boolean} allSystems          - show events through all systems
 * @param {boolean} withCountRunning    - add count running to request?
 * @return {Promise}
 */
export const loadEventsForSwiper = (type, cursor, limit, eventsFilter = '', allSystems = false, withCountRunning = true) => {
    const {
        REACT_APP_API_URL,
        REACT_APP_SYSTEM,
    } = config

    const requests = []

    eventsFilter = (eventsFilter && typeof eventsFilter === 'string') ? eventsFilter.replace('take=next', '') : eventsFilter

    let dateType = 'running'

    switch (type) {
        case 'next':
            dateType = 'next'
            break
        case 'past':
            dateType = 'prev&history=true'
            break
        case 'running,next':
            dateType = 'running,next'
            break
    }

    const includes = [
        'sports',
        'locations',
        'additionalInfo',
        'headerImage',
    ].join(',')

    if (limit) {
        requests.push(window.axios.get(`${REACT_APP_API_URL}api/events?dateType=${dateType}${allSystems ? '' : `&system=${REACT_APP_SYSTEM}`}&tz=${moment.tz.guess()}&include=${includes}&limit=${limit + 1}&cursor=${cursor || 0}&${eventsFilter}`))

        if (withCountRunning) {
            requests.push(window.axios.get(`${REACT_APP_API_URL}api/events/count?publishable=true&dateType=running${allSystems ? '' : `&system=${REACT_APP_SYSTEM}`}&${eventsFilter}`))
        }
    }

    if (!requests) {
        return new Promise(resolve => resolve())
    }

    return window.axios.all(requests)
        .then(result => {
            const events = result[0]?.data?.data
            const runningEventsTotal = result[1]?.data?.data?.total

            let typeEvents = ''

            switch (type) {
                case 'actual':
                    typeEvents = 'running'
                    break
                case 'next':
                case 'running,next':
                    typeEvents = 'events'
                    break
                case 'past':
                    typeEvents = 'prev'
                    break
            }

            let mappedEvents = {
                events: events,
            }

            if (typeEvents === 'running') {
                mappedEvents = {
                    ...mappedEvents,
                    total: runningEventsTotal,
                }
            }

            store.dispatch({
                type: 'EVENTS_APPEND_DATA',
                element: typeEvents,
                data: mappedEvents,
            })

            return new Promise(resolve => resolve(result))
        })
}
